import {
  AUCTION_COUNTDOWN_MS,
  BOARD,
  COLOR_GROUPS,
  GROUP_INFO,
  MIN_AUCTION_BID,
  RAILROAD_FIELDS,
  UTILITY_FIELDS,
} from "@grundbesitz/shared";
import type {
  AuctionState,
  ColorGroup,
  Debt,
  EngineAction,
  GameEvent,
  PlayerAction,
  PlayerId,
  StateView,
  Trade,
  TradeSide,
} from "@grundbesitz/shared";
import {
  allStreetsSold,
  checkBuildRules,
  computeRent,
  debtTotal,
  debtsOf,
  demandMore,
  getPlayer,
  highestBid,
  houseCostOf,
  maxRaisable,
  minimumBid,
  mortgageInterest,
  mortgageValue,
  ownedFields,
  ownsGroup,
  unmortgageCost,
  validateAction,
} from "@grundbesitz/engine";
import { formatMoney } from "./format";

/**
 * Pure logic behind the asset, trade and auction panels. Nothing here decides a rule:
 * legality always comes from the engine's `validateAction`, this file only shapes it for display.
 */

// ---------------------------------------------------------------- shared helpers

/** Asks the engine whether `pid` may send `action` now. Returns the German reason or null. */
export function actionError(s: StateView, pid: PlayerId, action: PlayerAction): string | null {
  const error = validateAction(s, { ...action, player: pid } as EngineAction);
  return error ? error.message : null;
}

const nameOf = (s: StateView, id: PlayerId | null | undefined): string =>
  (id && getPlayer(s, id)?.name) || (id === "bank" ? "Bank" : "Unbekannt");

// ---------------------------------------------------------------- groups

export type AssetGroupKey = ColorGroup | "railroad" | "utility";

export interface AssetGroupMeta {
  key: AssetGroupKey;
  name: string;
  /** Data color (hex). Never the only carrier of the information, the name always goes with it. */
  color: string;
  fields: readonly number[];
  isStreetGroup: boolean;
}

export const ASSET_GROUPS: readonly AssetGroupMeta[] = [
  ...COLOR_GROUPS.map((g) => ({
    key: g,
    name: GROUP_INFO[g].name,
    color: GROUP_INFO[g].color,
    fields: GROUP_INFO[g].fields,
    isStreetGroup: true,
  })),
  { key: "railroad", name: "Bahnhöfe", color: "#56606b", fields: RAILROAD_FIELDS, isStreetGroup: false },
  { key: "utility", name: "Werke", color: "#9a8f5a", fields: UTILITY_FIELDS, isStreetGroup: false },
];

export function assetGroupOf(field: number): AssetGroupMeta | null {
  return ASSET_GROUPS.find((g) => g.fields.includes(field)) ?? null;
}

export interface OwnedGroup {
  meta: AssetGroupMeta;
  /** Fields of the group the player owns, in board order. */
  owned: number[];
  complete: boolean;
  /** Fields of the group somebody else (or nobody) owns. */
  missing: { field: number; owner: PlayerId | null }[];
}

/** The player's properties grouped by color group, railroads and utilities last. Empty groups are left out. */
export function ownedGroups(s: StateView, pid: PlayerId): OwnedGroup[] {
  const mine = new Set(ownedFields(s, pid));
  const out: OwnedGroup[] = [];
  for (const meta of ASSET_GROUPS) {
    const owned = meta.fields.filter((f) => mine.has(f));
    if (owned.length === 0) continue;
    out.push({
      meta,
      owned,
      complete: owned.length === meta.fields.length,
      missing: meta.fields.filter((f) => !mine.has(f)).map((f) => ({ field: f, owner: s.properties[f].owner })),
    });
  }
  return out;
}

export interface GroupStatus {
  tone: "ok" | "warn" | "info";
  headline: string;
  detail: string | null;
}

/** One line on whether the group is complete and why building does (not) work yet. */
export function groupStatus(s: StateView, pid: PlayerId, g: OwnedGroup): GroupStatus {
  const total = g.meta.fields.length;
  if (!g.meta.isStreetGroup) {
    const kind = g.meta.key === "railroad" ? "Bahnhöfe" : "Werke";
    return {
      tone: "info",
      headline: `${g.owned.length} von ${total} ${kind}`,
      detail:
        g.meta.key === "railroad"
          ? "Die Miete steigt mit jedem Bahnhof, den du besitzt."
          : "Miete: Würfelsumme × 4, mit beiden Werken × 10.",
    };
  }
  if (!g.complete) {
    const missing = g.missing
      .map((m) => `${BOARD[m.field].name} (${m.owner ? nameOf(s, m.owner) : "Bank"})`)
      .join(", ");
    return {
      tone: "info",
      headline: `${g.owned.length} von ${total} Straßen`,
      detail: `Bauen geht erst mit der ganzen Gruppe. Es fehlt: ${missing}.`,
    };
  }
  const group = g.meta.key as ColorGroup;
  const anyMortgage = g.owned.some((f) => s.properties[f].mortgaged);
  if (anyMortgage) {
    return {
      tone: "warn",
      headline: "Komplette Gruppe",
      detail: "Bauen ist gesperrt, solange eine Straße der Gruppe belastet ist. Löse die Hypothek zuerst auf.",
    };
  }
  if (debtsOf(s, pid).length > 0) {
    return { tone: "warn", headline: "Komplette Gruppe", detail: "Im Schuldenmodus kannst du nicht bauen." };
  }
  if (s.options.houseRules.buildAfterAllStreetsSold && !allStreetsSold(s)) {
    return {
      tone: "info",
      headline: "Komplette Gruppe",
      detail: "Gebaut werden darf erst, wenn alle Straßen verkauft sind (Hausregel).",
    };
  }
  const buildings = g.owned.map((f) => s.properties[f].buildings);
  if (buildings.every((b) => b === 5)) {
    return { tone: "ok", headline: "Komplette Gruppe", detail: "Voll ausgebaut." };
  }
  if (buildings.every((b) => b === 0)) {
    return {
      tone: "ok",
      headline: "Komplette Gruppe",
      detail: `Grundmiete ist verdoppelt. Ein Haus kostet ${formatMoney(GROUP_INFO[group].houseCost)}, gebaut wird gleichmäßig.`,
    };
  }
  return { tone: "ok", headline: "Komplette Gruppe", detail: "Gebaut wird gleichmäßig." };
}

/** Current rent of an owned field as a short text. */
export function currentRent(s: StateView, field: number): string {
  const f = BOARD[field];
  const prop = s.properties[field];
  if (!prop || prop.owner === null) return "";
  if (prop.mortgaged) return "Keine Miete (belastet)";
  switch (f.kind) {
    case "STREET": {
      const rent = formatMoney(computeRent(s, field, 0));
      if (prop.buildings === 0 && ownsGroup(s, prop.owner, f.group)) return `${rent} (Grundmiete × 2)`;
      return rent;
    }
    case "RAILROAD":
      return formatMoney(computeRent(s, field, 0));
    case "UTILITY":
      return `Würfelsumme × ${computeRent(s, field, 1)}`;
    default:
      return "";
  }
}

// ---------------------------------------------------------------- field actions

export type AssetActionKind = "BUILD" | "SELL_BUILDING" | "MORTGAGE" | "UNMORTGAGE";

export interface AssetAction {
  kind: AssetActionKind;
  /** Button text with the amount: "Haus bauen (M 200)". */
  label: string;
  action: PlayerAction;
  /** Engine reason why the action is not possible right now, null when it is. */
  error: string | null;
}

/** What selling the top building of `field` pays. A hotel comes down to as many houses as the bank has (max. 4). */
export function sellGain(s: StateView, field: number): number {
  const half = houseCostOf(field) / 2;
  if (s.properties[field].buildings === 5) {
    const houses = Math.min(4, s.bank.houses);
    return half * (1 + (4 - houses));
  }
  return half;
}

/** Buttons for one owned field. Enabled state and reasons come from the engine. */
export function fieldActions(s: StateView, pid: PlayerId, field: number): AssetAction[] {
  const f = BOARD[field];
  const prop = s.properties[field];
  if (!prop || prop.owner !== pid) return [];
  const make = (kind: AssetActionKind, label: string): AssetAction => {
    const action = { type: kind, field } as PlayerAction;
    return { kind, label, action, error: actionError(s, pid, action) };
  };
  if (prop.mortgaged) {
    return [make("UNMORTGAGE", `Hypothek auflösen (${formatMoney(unmortgageCost(field))})`)];
  }
  const out: AssetAction[] = [];
  if (f.kind === "STREET") {
    if (ownsGroup(s, pid, f.group) && prop.buildings < 5) {
      const check = checkBuildRules(s, pid, field);
      const noun = check.kind === "hotel" ? "Hotel" : "Haus";
      const supply = check.kind === "hotel" ? s.bank.hotels : s.bank.houses;
      const registers = supply === 1 || (s.buildWindow !== null && s.buildWindow.kind === check.kind);
      out.push(
        make(
          "BUILD",
          registers
            ? `Für das letzte ${noun} anmelden (${formatMoney(f.houseCost)})`
            : `${noun} bauen (${formatMoney(f.houseCost)})`,
        ),
      );
    }
    if (prop.buildings > 0) {
      const noun = prop.buildings === 5 ? "Hotel" : "Haus";
      out.push(make("SELL_BUILDING", `${noun} verkaufen (+${formatMoney(sellGain(s, field))})`));
    }
  }
  out.push(make("MORTGAGE", `Hypothek aufnehmen (+${formatMoney(mortgageValue(field))})`));
  return out;
}

/**
 * A blocker that hits every asset action of the viewer at once (running auction, open mortgage
 * decision, somebody else's debt). Shown once at the top instead of on every button.
 */
export function lockNotice(s: StateView, pid: PlayerId): string | null {
  const owned = ownedFields(s, pid);
  if (owned.length === 0) return null;
  const error = validateAction(s, { type: "MORTGAGE", field: owned[0], player: pid });
  return error && error.code === "LOCKED" ? error.message : null;
}

/** Note for the hotel sale when the bank cannot hand back four houses. */
export function hotelSaleNote(s: StateView, field: number): string | null {
  if (s.properties[field].buildings !== 5 || s.bank.houses >= 4) return null;
  return `Die Bank hat nur ${s.bank.houses} ${s.bank.houses === 1 ? "Haus" : "Häuser"}: Das Hotel wird darauf zurückgebaut, der Rest wird mit ausgezahlt.`;
}

// ---------------------------------------------------------------- build window

export interface BuildWindowInfo {
  kind: "house" | "hotel";
  noun: string;
  closing: boolean;
  /** The viewer's own registration. */
  mine: { field: number } | null;
  /** Other players who registered. */
  others: { playerId: PlayerId; name: string; field: number }[];
}

export function buildWindowInfo(s: StateView, pid: PlayerId | null): BuildWindowInfo | null {
  const w = s.buildWindow;
  if (!w) return null;
  const mine = w.requests.find((r) => r.playerId === pid);
  return {
    kind: w.kind,
    noun: w.kind === "house" ? "Haus" : "Hotel",
    closing: w.closing,
    mine: mine ? { field: mine.field } : null,
    others: w.requests
      .filter((r) => r.playerId !== pid)
      .map((r) => ({ playerId: r.playerId, name: nameOf(s, r.playerId), field: r.field })),
  };
}

// ---------------------------------------------------------------- debt mode

export interface DebtView {
  debts: Debt[];
  total: number;
  cash: number;
  /** total - cash, what is still missing. */
  shortfall: number;
  /** Cash plus everything sellable and mortgageable. */
  raisable: number;
  /** Even the maximum does not cover the debt: bankruptcy is possible. */
  hopeless: boolean;
}

export function debtView(s: StateView, pid: PlayerId): DebtView | null {
  const debts = debtsOf(s, pid);
  if (debts.length === 0) return null;
  const total = debtTotal(s, pid);
  const cash = getPlayer(s, pid)?.cash ?? 0;
  const raisable = maxRaisable(s, pid);
  return { debts, total, cash, shortfall: Math.max(0, total - cash), raisable, hopeless: raisable < total };
}

export interface RaiseOption {
  kind: "MORTGAGE" | "SELL_BUILDING";
  field: number;
  /** Cash the option brings in. */
  gain: number;
  /** What it costs you: interest when you lift the mortgage later, or the lost half of the build price. */
  sacrifice: number;
  label: string;
  action: PlayerAction;
  /** The option is part of the shortest cheap-first path that covers the shortfall. */
  needed: boolean;
}

/**
 * Ways to raise money, cheapest first. Purely a display order: nothing is executed for the player.
 * Options the engine currently refuses (uneven selling, mortgage under buildings) are left out.
 */
export function raiseSuggestions(s: StateView, pid: PlayerId): RaiseOption[] {
  const view = debtView(s, pid);
  const options: Omit<RaiseOption, "needed">[] = [];
  for (const field of ownedFields(s, pid)) {
    const prop = s.properties[field];
    const name = BOARD[field].name;
    if (!prop.mortgaged) {
      const action: PlayerAction = { type: "MORTGAGE", field };
      if (!actionError(s, pid, action)) {
        options.push({
          kind: "MORTGAGE",
          field,
          gain: mortgageValue(field),
          sacrifice: mortgageInterest(field),
          label: `${name} belasten`,
          action,
        });
      }
    }
    if (prop.buildings > 0) {
      const action: PlayerAction = { type: "SELL_BUILDING", field };
      if (!actionError(s, pid, action)) {
        options.push({
          kind: "SELL_BUILDING",
          field,
          gain: sellGain(s, field),
          sacrifice: houseCostOf(field) / 2,
          label: `${prop.buildings === 5 ? "Hotel" : "Haus"} auf ${name} verkaufen`,
          action,
        });
      }
    }
  }
  options.sort((a, b) => a.sacrifice - b.sacrifice || a.gain - b.gain || a.field - b.field);
  let covered = 0;
  const missing = view?.shortfall ?? 0;
  return options.map((o) => {
    const needed = covered < missing;
    covered += o.gain;
    return { ...o, needed };
  });
}

// ---------------------------------------------------------------- trades

export const emptyTradeSide = (): TradeSide => ({ cash: 0, fields: [], jailCards: [] });

export const isTradeSideEmpty = (side: TradeSide): boolean =>
  side.cash === 0 && side.fields.length === 0 && side.jailCards.length === 0;

export interface TradableField {
  field: number;
  mortgaged: boolean;
  /** Why the field cannot be part of an offer, null when it can. */
  blocked: string | null;
}

/** Fields of `pid` for the trade editor. Groups with buildings are blocked (the engine rejects them too). */
export function tradableFields(s: StateView, pid: PlayerId): TradableField[] {
  return ownedFields(s, pid).map((field) => {
    const f = BOARD[field];
    const hasBuildings =
      f.kind === "STREET" && GROUP_INFO[f.group].fields.some((i) => s.properties[i].buildings > 0);
    return {
      field,
      mortgaged: s.properties[field].mortgaged,
      blocked: hasBuildings ? "Gebäude erst an die Bank verkaufen." : null,
    };
  });
}

/** Card ids start with "ev" (Ereignis) or "gm" (Gemeinschaft), see `cards.de.ts`. */
export function jailCardLabel(cardId: string): string {
  return cardId.startsWith("ev") ? "Freikarte (Ereignis)" : "Freikarte (Gemeinschaft)";
}

export function describeSide(side: TradeSide): string {
  const parts = side.fields.map((f) => BOARD[f].name);
  if (side.cash > 0) parts.push(formatMoney(side.cash));
  if (side.jailCards.length > 0) {
    parts.push(side.jailCards.length === 1 ? "1 Freikarte" : `${side.jailCards.length} Freikarten`);
  }
  return parts.length > 0 ? parts.join(" + ") : "nichts";
}

/** "Du gibst: Badstraße + M 50. Du bekommst: Seestraße" */
export function tradeSummary(give: TradeSide, get: TradeSide): string {
  return `Du gibst: ${describeSide(give)}. Du bekommst: ${describeSide(get)}`;
}

/** Warnings for the mortgaged fields in `side`: whoever receives them has to decide at once. */
export function mortgagedNotes(s: StateView, side: TradeSide): string[] {
  return side.fields
    .filter((f) => s.properties[f]?.mortgaged)
    .map(
      (f) =>
        `${BOARD[f].name}: Der neue Besitzer muss sofort auflösen (${formatMoney(unmortgageCost(f))}, Wert + 10 %) oder ${formatMoney(mortgageInterest(f))} Zinsen zahlen.`,
    );
}

/** Counter offer: the same deal from the recipient's point of view (what they were asked for becomes what they give). */
export function mirrorTrade(t: Trade): { give: TradeSide; get: TradeSide } {
  const copy = (x: TradeSide): TradeSide => ({ cash: x.cash, fields: [...x.fields], jailCards: [...x.jailCards] });
  return { give: copy(t.get), get: copy(t.give) };
}

export const DEMAND_STEPS = [50, 100, 200] as const;

/** "Nachfordern": the same deal sent back with `extra` more cash for the viewer. Null when the partner cannot pay that much. */
export function demandAction(s: StateView, pid: PlayerId, t: Trade, extra: number): PlayerAction | null {
  const action: PlayerAction = { type: "COUNTER_TRADE", tradeId: t.id, ...demandMore(t, extra) };
  return actionError(s, pid, action) === null ? action : null;
}

export const sortTradesNewestFirst = (trades: readonly Trade[]): Trade[] => [...trades].sort((a, b) => b.id - a.id);

export function proposeError(s: StateView, pid: PlayerId, to: PlayerId | null, give: TradeSide, get: TradeSide): string | null {
  if (!to) return "Wähle einen Mitspieler.";
  return actionError(s, pid, { type: "PROPOSE_TRADE", to, give, get });
}

export function counterError(s: StateView, pid: PlayerId, tradeId: number, give: TradeSide, get: TradeSide): string | null {
  return actionError(s, pid, { type: "COUNTER_TRADE", tradeId, give, get });
}

/** Why "Annehmen" is blocked for the recipient, null when it works. */
export function acceptError(s: StateView, pid: PlayerId, tradeId: number): string | null {
  return actionError(s, pid, { type: "ACCEPT_TRADE", tradeId });
}

// ---------------------------------------------------------------- auction

export const QUICK_BID_STEPS = [1, 10, 50, 100] as const;

export interface QuickBid {
  label: string;
  amount: number;
  affordable: boolean;
}

/** Quick buttons: +1/+10/+50/+100 on the highest bid (or on the start bid 10). Without a bid the start bid comes first. */
export function quickBids(s: StateView, cash: number): QuickBid[] {
  const high = highestBid(s);
  const base = high ? high.amount : MIN_AUCTION_BID;
  const out: QuickBid[] = [];
  if (!high) out.push({ label: formatMoney(MIN_AUCTION_BID), amount: MIN_AUCTION_BID, affordable: cash >= MIN_AUCTION_BID });
  for (const step of QUICK_BID_STEPS) out.push({ label: `+${step}`, amount: base + step, affordable: cash >= base + step });
  return out;
}

/** Reads a bid typed by a person: "12", "M 1.200", " 50 ". Returns null unless it is a whole positive number. */
export function parseBid(text: string): number | null {
  const digits = text.replace(/[\s.M]/gi, "");
  if (!/^\d{1,7}$/.test(digits)) return null;
  const n = Number(digits);
  return n >= 1 && n <= 1_000_000 ? n : null;
}

export interface BidCheck {
  ok: boolean;
  /** German text for the reason, or the "Du hast M 120" cash hint. */
  message: string | null;
}

/** Whether `pid` may bid `amount` now. Higher-than-cash bids get the cash hint. */
export function bidCheck(s: StateView, pid: PlayerId, amount: number | null): BidCheck {
  const cash = getPlayer(s, pid)?.cash ?? 0;
  if (amount === null) {
    // Ask about the smallest bid so state-level blockers (debt, passed, leading) show up.
    const blocker = actionError(s, pid, { type: "BID", amount: Math.max(1, minimumBid(s)) });
    const onlyCash = blocker?.startsWith("Du kannst nicht mehr bieten");
    return { ok: false, message: blocker && !onlyCash ? blocker : `Du hast ${formatMoney(cash)}.` };
  }
  const error = actionError(s, pid, { type: "BID", amount });
  if (!error) return { ok: true, message: null };
  if (amount > cash) return { ok: false, message: `Du hast ${formatMoney(cash)}.` };
  return { ok: false, message: error };
}

export type ParticipantStatus = "LEADING" | "BIDDING" | "PASSED" | "DEBT";

export interface AuctionParticipant {
  playerId: PlayerId;
  name: string;
  status: ParticipantStatus;
  /** Their own highest bid, null when they have not bid. */
  lastBid: number | null;
}

export function auctionParticipants(s: StateView): AuctionParticipant[] {
  const a = s.auction;
  if (!a) return [];
  const high = highestBid(s);
  return a.eligible.map((playerId) => {
    const own = a.bids.filter((b) => b.playerId === playerId).map((b) => b.amount);
    let status: ParticipantStatus = "BIDDING";
    if (a.passed.includes(playerId)) status = "PASSED";
    else if (high?.playerId === playerId) status = "LEADING";
    else if (debtsOf(s, playerId).length > 0) status = "DEBT";
    return { playerId, name: nameOf(s, playerId), status, lastBid: own.length > 0 ? Math.max(...own) : null };
  });
}

/** Latest bids first. */
export function recentBids(a: AuctionState, limit = 6): { playerId: PlayerId; amount: number }[] {
  return [...a.bids].reverse().slice(0, limit);
}

/** 0..1, the share of the countdown that is left. */
export function countdownFraction(endsAt: number | null, now: number, totalMs = AUCTION_COUNTDOWN_MS): number {
  if (endsAt === null) return 0;
  return Math.min(1, Math.max(0, (endsAt - now) / totalMs));
}

export interface AuctionSubject {
  /** Short title: "Seestraße" or "Haus". */
  title: string;
  /** Field for the property card, null for building auctions. */
  field: number | null;
  /** Building auctions: who wants to build where. */
  requests: { playerId: PlayerId; name: string; field: number }[];
}

export function auctionSubject(s: StateView, a: AuctionState, pid: PlayerId | null): AuctionSubject {
  if (a.kind === "PROPERTY" && a.field !== null) {
    return { title: BOARD[a.field].name, field: a.field, requests: [] };
  }
  const noun = a.buildingKind === "hotel" ? "Hotel" : "Haus";
  const mine = a.requests.find((r) => r.playerId === pid);
  return {
    title: mine ? `${noun} für ${BOARD[mine.field].name}` : noun,
    field: null,
    requests: a.requests.map((r) => ({ playerId: r.playerId, name: nameOf(s, r.playerId), field: r.field })),
  };
}

export type AuctionEnded = Extract<GameEvent, { type: "AUCTION_ENDED" }>;

/** "Ben ersteigert Seestraße für M 165" */
export function describeAuctionResult(
  ev: AuctionEnded,
  winnerName: string | null,
  previous: { buildingKind: "house" | "hotel" | null } | null,
): string {
  const what =
    ev.kind === "PROPERTY" && ev.field !== null
      ? BOARD[ev.field].name
      : previous?.buildingKind === "hotel"
        ? "ein Hotel"
        : "ein Haus";
  if (ev.winnerId === null || winnerName === null) {
    return ev.kind === "PROPERTY" ? `Kein Gebot: ${what} bleibt frei.` : `Kein Gebot: ${what} wird nicht vergeben.`;
  }
  return `${winnerName} ersteigert ${what} für ${formatMoney(ev.amount)}`;
}
